import { GovernanceType } from 'Models/Governance';

import { GenericStatus, Regulation } from './ComplianceRequirements';
import { UploadedFile } from './Files';
import { Effectiveness } from './OperationalControls';

/** Represents generic structured AI output used for programmatically creating documents or rendering on a page. */
export interface AiDocument {
    section_list: AiDocumentSection[];
    title_string: string;
    subtitle_string?: string;
}
export interface AiDocumentSection {
    content_list: AiDocumentContent[];
    key: string;
    section_header_string: string;
    subsection_list: AiDocumentSubsection[];
}
interface AiDocumentSubsection {
    content_list: AiDocumentContent[];
    subsection_header_string: string;
}
export type AiDocumentContent = Partial<AiDocumentTextContent & AiDocumentBulletListContent & AiDocumentNumberListContent & AiDocumentFieldListContent>;
interface AiDocumentTextContent {
    text_string: string;
}
interface AiDocumentBulletListContent {
    bullet_list: AiDocumentTextContent[];
}
interface AiDocumentNumberListContent {
    number_list: AiDocumentTextContent[];
}
interface AiDocumentFieldListContent {
    field_list: AiDocumentFieldContent[];
}
interface AiDocumentFieldContent {
    field_name: string;
    field_value: string;
}

/** Represents the different types of documents that the AI Document Analyzer feature can process. */
export enum AiDocAnalyzerType {
    GENERIC = 'Generic',
    QUESTIONNAIRE = 'Questionnaire',
    SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT = 'System and Organization Controls (SOC) Report',
}

export enum AiContentAdvisorFeature {
    GOVERNANCE_AND_COMPLIANCE_CONTROL_ASSESSMENT_OWNER_COMMENTS = 'Governance and Compliance - Control Assessment - Owner Comments',
    GOVERNANCE_AND_COMPLIANCE_CONTROL_ASSESSMENT_REVIEWER_COMMENTS = 'Governance and Compliance - Control Assessment - Reviewer Comments',
    GOVERNANCE_AND_COMPLIANCE_CONTROL_DESCRIPTION = 'Governance and Compliance - Control - Description',
    GOVERNANCE_AND_COMPLIANCE_CONTROL_GROUP_DESCRIPTION = 'Governance and Compliance - Control Group - Description',
    GOVERNANCE_AND_COMPLIANCE_EXCEPTION_CLOSURE_STATEMENT = 'Governance and Compliance - Exception - Closure Statement',
    GOVERNANCE_AND_COMPLIANCE_EXCEPTION_COMPENSATING_CONTROLS = 'Governance and Compliance - Exception - Compensating Controls',
    GOVERNANCE_AND_COMPLIANCE_EXCEPTION_DESCRIPTION = 'Governance and Compliance - Exception - Description',
    GOVERNANCE_AND_COMPLIANCE_EXCEPTION_REMEDIATION_PLAN = 'Governance and Compliance - Exception - Remediation Plan',
    GOVERNANCE_AND_COMPLIANCE_EXCEPTION_RISK_ASSESSMENT = 'Governance and Compliance - Exception - Risk Assessment',
    GOVERNANCE_AND_COMPLIANCE_GOVERNANCE_DESCRIPTION = 'Governance and Compliance - Governance - Description',
    GOVERNANCE_AND_COMPLIANCE_GOVERNANCE_CHANGELOG = 'Governance and Compliance - Governance - Changelog',
    GOVERNANCE_AND_COMPLIANCE_ISSUE_CLOSURE_STATEMENT = 'Governance and Compliance - Issue - Closure Statement',
    GOVERNANCE_AND_COMPLIANCE_ISSUE_DESCRIPTION = 'Governance and Compliance - Issue - Description',
    GOVERNANCE_AND_COMPLIANCE_ISSUE_REMEDIATION_PLAN = 'Governance and Compliance - Issue - Remediation Plan',
    GOVERNANCE_AND_COMPLIANCE_ISSUE_RISK_ASSESSMENT = 'Governance and Compliance - Issue - Risk Assessment',
    RISK_MANAGEMENT_ACTION_DESCRIPTION = 'Risk Management - Action - Description',
    RISK_MANAGEMENT_RISK_DESCRIPTION = 'Risk Management - Risk - Description',
    RISK_MANAGEMENT_RISK_TREATMENT_PLAN_DESCRIPTION = 'Risk Management - Risk - Treatment Plan Description',
    TPRM_ASSESSMENT_CONTROL_COMMENTS = 'Third-Party Risk Management - Assessment - Control - Comments',
    TPRM_ASSESSMENT_REVIEW_ADDITIONAL_INFORMATION = 'Third-Party Risk Management - Assessment - Review - Additional Information',
    TPRM_FINAL_REVIEW_ADDITIONAL_INFORMATION = 'Third-Party Risk Management - Final Review - Additional Information',
    TPRM_IRQ_OVERALL_COMMENTS = 'Third-Party Risk Management - IRQ - Comments',
    TPRM_IRQ_SECTION_COMMENTS = 'Third-Party Risk Management - IRQ - Section - Comments',
    TPRM_ISSUE_DESCRIPTION = 'Third-Party Risk Management - Issue - Description', // GOVERNANCE_AND_COMPLIANCE_ISSUE_DESCRIPTION leads to the AI model being prompted to check whether the user should be creating a risk instead of an issue. We use a distinct enum member for TPRM issues because advising the user to create a risk instead would be misleading; risks in SummIT cannot be connected to third parties.
    TPRM_SERVICE_DESCRIPTION = 'Third-Party Risk Management - Service - Description',
    UCRM_ASSESSMENT_CONTROL_COMMENTS = 'AI Governance - Assessment - Control - Comments',
    UCRM_ASSESSMENT_REVIEW_ADDITIONAL_INFORMATION = 'AI Governance - Assessment - Review - Additional Information',
    UCRM_FINAL_REVIEW_ADDITIONAL_INFORMATION = 'AI Governance - Final Review - Additional Information',
    UCRM_IRQ_OVERALL_COMMENTS = 'AI Governance - IRQ - Comments',
    UCRM_IRQ_SECTION_COMMENTS = 'AI Governance - IRQ - Section - Comments',
    UCRM_USE_CASE_DESCRIPTION = 'AI Governance - Use Case - Description',
}

/** Request to invoke the AI Content Advisor. */
export interface AiContentAdvisorRequest {
    content: string;
    feature?: AiContentAdvisorFeature;
}

/** Response from the AI Content Advisor. */
export interface AiContentAdvisorResponse {
    feedback: string;
    recommendation?: string;
}

/** Represents an existing AI Document Analysis once it has been created. */
export interface AiDocAnalysis {
    created_by: string;
    created_timestamp: string;
    document_type: AiDocAnalyzerType;
    id: string;
    input_file: UploadedFile;
    summary?: AiDocument;
    summary_file?: UploadedFile;

    // Attributes that are relevant for AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT
    create_exceptions: boolean;
    expiration_date?: string;
    third_party_id?: string;
    third_party_folder_id?: string;
}

/** A request to create a new AI Document Analysis. */
export interface AiDocAnalyzerCreateRequest {
    document_type: AiDocAnalyzerType;
    input_file_id: string;
    input_filename: string;

    // Attributes that are relevant for AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT
    create_exceptions: boolean;
    expiration_date?: string;
    third_party_id?: string;
    third_party_folder_id?: string;
}

/** Represents an existing AI-generated document once it has been created. */
export interface AiGeneratedDoc {
    created_by: string;
    created_timestamp: string;
    description: string;
    id: string;
    title: string;
    type: GovernanceType;
    input_file?: UploadedFile;
    output_file?: UploadedFile; // Optional only because a database record is created before the output file is generated. Once the GenAI model has generated the output file, this attribute is set.
    regulation?: Regulation;
}

/** A request to create a new AI-generated document. */
export interface AiDocGeneratorCreateRequest {
    description: string;
    title: string;
    type: GovernanceType;
    input_file_id?: string;
    input_filename?: string;
    regulation?: Regulation;
}

/**
 * A request to start a multi step process utilizing artificial intelligence (AI) to provide recommendations for a due diligence assessment (of a TPRM third-party service or a UCRM use case).
 * @param include_client_responses Whether to include the Client's responses to the assessment in the GenAI model prompt.
 * @param include_third_party_responses Whether to include the responses to the questionnaire (from the third party for TPRM, or from the questionnaire respondents for UCRM) in the GenAI model prompt.
 */
export interface AiDueDiligenceAnalyzeAssessmentRequest {
    include_client_responses: boolean;
    include_third_party_responses: boolean;
}

export interface AiOperationalControlAssessmentControlAnalysis {
    control_effectiveness_rating: Effectiveness;
    control_identifier: string;
    summary: string;
    improvements: string[];
    key_observations: string[];
}

export interface AiOperationalControlAssessmentComplianceRequirementAnalysis {
    regulation: string;
    identifier: string;
    status: GenericStatus.MET | GenericStatus.NOT_MET;
    explanation: string;
    suggestions: string[];
    assumptions?: string;
}

export interface AiOperationalControlAssessmentFinalResult {
    control_analysis: AiOperationalControlAssessmentControlAnalysis;
    requirement_analyses: AiOperationalControlAssessmentComplianceRequirementAnalysis[];
    timestamp: string;
}

export enum AiOperationalControlAssessmentGenerationStatus {
    IN_PROGRESS = 'In progress',
    SUCCESS = 'Success',
    FAILURE = 'Failure',
}

export interface AiOperationalControlAssessment {
    final_result?: AiOperationalControlAssessmentFinalResult;
    generation_status?: AiOperationalControlAssessmentGenerationStatus;
}

export enum AiDueDiligenceAnalysisStatus {
    IN_PROGRESS = 'In progress',
    FAILED = 'Failed',
    SUCCEEDED = 'Succeeded',
}

export interface AiDueDiligenceAssessmentAnalysisMetadata {
    created_timestamp: string;
    include_client_responses: boolean;
    include_third_party_responses: boolean;
    status: AiDueDiligenceAnalysisStatus;

    created_by?: string; // Present if and only if a user manually requested an AI review (as opposed to Auto-Advance).
}

export interface AiDueDiligenceFinalReviewAnalysisMetadata {
    created_timestamp: string;
    status: AiDueDiligenceAnalysisStatus;

    created_by?: string; // Present if and only if a user manually requested an AI review (as opposed to Auto-Advance).
}

export interface DueDiligenceAssessmentControlAssessmentAi {
    comments: string;
    created_timestamp: string;
    recommended_effectiveness: Effectiveness;
}

export interface DueDiligenceReviewAi {
    comments: string;
    recommended_effectiveness: Effectiveness;
    recommended_residual_risk?: number; // Before the release of SummIT's multiple third-party due diligence assessments, SummIT Guide did not recommend residual risk. So, while this will always be present for new analyses, historical items will not have it.
}
